/**
 * Shared building blocks for the report pages: the data-loading hook,
 * URL-backed filter helpers, layout, filters, tables, and state messages.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, secondaryButtonClass } from '../../components/formStyles';
import { ICustomersApi } from '../../api';
import { Toast } from '../../components/Toast';

// ---------------------------------------------------------------------------
// Data + URL state
// ---------------------------------------------------------------------------

export interface ReportState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Loads a report whenever `requestKey` changes. A null key means the filters
 * are invalid (e.g. start > end): nothing is requested and nothing is shown.
 * `load` is read through a ref so callers can pass an inline closure without
 * re-triggering the effect; the key carries every input that matters.
 */
export function useReportData<T>(requestKey: string | null, load: () => Promise<T>, fallbackError: string): ReportState<T> {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [reload, setReload] = useState(0);
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string | null }>({
    data: null,
    loading: requestKey !== null,
    error: null,
  });

  useEffect(() => {
    if (requestKey === null) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    let active = true;
    setState((previous) => ({ data: previous.data, loading: true, error: null }));
    loadRef.current()
      .then((data) => { if (active) setState({ data, loading: false, error: null }); })
      .catch((cause) => { if (active) setState({ data: null, loading: false, error: getErrorMessage(cause, fallbackError) }); });
    return () => { active = false; };
  }, [requestKey, reload, fallbackError]);

  const retry = useCallback(() => setReload((value) => value + 1), []);
  return { ...state, retry };
}

/** Query-string setter; any filter change other than `page` resets to page 1. */
export function useReportParams() {
  const [params, setParams] = useSearchParams();
  const change = useCallback((next: Record<string, string>) => {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    if (!('page' in next)) updated.delete('page');
    setParams(updated);
  }, [params, setParams]);
  return { params, change };
}

/** Customer options for a filter select, paged in 100 at a time like JobListPage. */
export function useCustomerOptions(api: Pick<ICustomersApi, 'listCustomers'>) {
  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  useEffect(() => {
    let active = true;
    api.listCustomers(page, 100).then((result) => {
      if (!active) return;
      setCustomers((previous) => page === 1 ? result.data : [...previous, ...result.data.filter((customer) => !previous.some((saved) => saved.id === customer.id))]);
      setPages(Math.max(1, result.pagination.pages));
    }).catch(() => { /* The filter degrades to "All customers"; the report itself still loads. */ });
    return () => { active = false; };
  }, [api, page]);
  return { customers, hasMore: page < pages, loadMore: () => setPage((current) => current + 1) };
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function ReportPage({ title, intro, children }: { title: string; intro?: React.ReactNode; children: React.ReactNode }) {
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/reports" className="text-sm font-medium text-gray-500 hover:text-gray-900">&larr; Reports</Link>
      <h1 className="mt-2 text-2xl font-bold text-gray-900">{title}</h1>
      {intro && <p className="mt-1 mb-6 text-sm text-gray-600 max-w-3xl">{intro}</p>}
      {!intro && <div className="mb-6" />}
      <div className="space-y-6">{children}</div>
    </main>
  );
}

export function ReportCard({ title, children, actions }: { title?: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <section className="bg-white rounded-lg shadow p-5 sm:p-6">
      {(title || actions) && (
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function FilterGrid({ children }: { children: React.ReactNode }) {
  return <ReportCard><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div></ReportCard>;
}

export function DateField({ id, label, value, onChange, hint }: { id: string; label: string; value: string; onChange: (value: string) => void; hint?: string }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <input id={id} type="date" className={inputClass} value={value} onChange={(event) => onChange(event.target.value)} />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function SelectField({ id, label, value, onChange, children, footer }: {
  id: string; label: string; value: string; onChange: (value: string) => void; children: React.ReactNode; footer?: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <select id={id} className={inputClass} value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
      {footer}
    </div>
  );
}

export function CustomerFilter({ id, value, onChange, options }: {
  id: string; value: string; onChange: (value: string) => void; options: ReturnType<typeof useCustomerOptions>;
}) {
  const { customers, hasMore, loadMore } = options;
  return (
    <SelectField
      id={id}
      label="Customer"
      value={value}
      onChange={onChange}
      footer={hasMore && <button type="button" className="mt-1 py-2 sm:py-0 text-sm text-blue-700 underline" onClick={loadMore}>Load more customers</button>}
    >
      <option value="">All customers</option>
      {value && !customers.some((customer) => customer.id === value) && <option value={value}>Selected customer</option>}
      {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
    </SelectField>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function RangeError({ message }: { message: string | null }) {
  if (!message) return null;
  return <Toast kind="warning">{message} The report was not run.</Toast>;
}

/** Renders loading / error / (no data) states; children render only once data is present. */
export function ReportStatus<T>({ state, label, children }: { state: ReportState<T>; label: string; children: (data: T) => React.ReactNode }) {
  if (state.loading) return <ReportCard><p role="status" className="py-10 text-center text-gray-600">Loading {label}…</p></ReportCard>;
  if (state.error) {
    return (
      <>
        <Toast kind="error">{state.error}</Toast>
        <p className="text-gray-600">
          The {label} could not be loaded.{' '}
          <button type="button" onClick={state.retry} className="underline ml-2 py-2 sm:py-0">Retry</button>
        </p>
      </>
    );
  }
  if (!state.data) return null;
  return <>{children(state.data)}</>;
}

export function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-gray-600">{message}</td></tr>;
}

// ---------------------------------------------------------------------------
// Data display
// ---------------------------------------------------------------------------

export function SummaryTiles({ tiles }: { tiles: Array<{ label: string; value: React.ReactNode; tone?: 'good' | 'bad' }> }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((tile) => (
        <div key={tile.label} className="bg-white rounded-lg shadow p-5">
          <p className="text-sm text-gray-500">{tile.label}</p>
          <p className={`mt-1 text-2xl font-semibold ${tile.tone === 'good' ? 'text-green-700' : tile.tone === 'bad' ? 'text-red-700' : 'text-gray-900'}`}>{tile.value}</p>
        </div>
      ))}
    </div>
  );
}

export interface Column {
  label: string;
  numeric?: boolean;
}

// Cells only stay on one line from md up; below that the table renders as cards (responsive-table) and values must wrap.
const cellBase = 'px-4 py-3 md:whitespace-nowrap';
export const cellClass = (numeric?: boolean) => `${cellBase} ${numeric ? 'text-right tabular-nums' : 'text-left'}`;

/**
 * Fills in `data-label` (from the column headers) on every body/footer cell that
 * lacks one, so callers' rows render as labelled cards on small screens. Cells a
 * caller already labelled keep their label; colspan cells (empty states, "Total"
 * spans) and columns with an empty header stay unlabelled and span the card.
 */
function labelCells(table: HTMLTableElement, columns: Column[]) {
  const rows = [...Array.from(table.tBodies), ...(table.tFoot ? [table.tFoot] : [])].flatMap((section) => Array.from(section.rows));
  rows.forEach((row) => {
    let index = 0;
    Array.from(row.cells).forEach((cell) => {
      const span = Math.max(1, cell.colSpan || 1);
      const label = span === 1 && cell.tagName === 'TD' ? columns[index]?.label : undefined;
      // Only touch labels this helper set (marked data-auto-label), never a caller's own.
      const ours = cell.hasAttribute('data-auto-label');
      if (ours || !cell.hasAttribute('data-label')) {
        if (label) { cell.setAttribute('data-label', label); cell.setAttribute('data-auto-label', ''); }
        else if (ours) { cell.removeAttribute('data-label'); cell.removeAttribute('data-auto-label'); }
      }
      index += span;
    });
  });
}

export function ReportTable({ columns, children, footer, caption }: { columns: Column[]; children: React.ReactNode; footer?: React.ReactNode; caption?: string }) {
  const tableRef = useRef<HTMLTableElement>(null);
  // Runs after every render so rows added or replaced by new data are labelled too.
  useLayoutEffect(() => { if (tableRef.current) labelCells(tableRef.current, columns); });
  return (
    <div className="md:overflow-x-auto">
      <table ref={tableRef} className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-gray-50">
          <tr>{columns.map((column) => <th key={column.label} scope="col" className={`${cellClass(column.numeric)} font-medium text-gray-600`}>{column.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-200">{children}</tbody>
        {footer && <tfoot className="bg-gray-50 font-semibold border-t-2 border-gray-300">{footer}</tfoot>}
      </table>
    </div>
  );
}

export function Pager({ pagination, onPage, noun }: { pagination: types.PaginationInfo; onPage: (page: number) => void; noun: string }) {
  const pages = Math.max(1, pagination.pages || 1);
  const page = pagination.page;
  return (
    <div className="flex flex-wrap justify-between items-center gap-3 mt-6">
      <span className="text-sm text-gray-600">{pagination.total} {noun}{pagination.total === 1 ? '' : 's'}</span>
      {pages > 1 && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <button type="button" className={secondaryButtonClass} disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
